import React, { useState } from 'react';
import Input from '../../components/Fields/Input';
import { Field } from '../../components/Fields/Field';
import * as yup from 'yup';
import 'yup-phone-lite';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { NetworkManager } from '../../services';
import { useOnboardingNavigate, useOnboardingStore } from './store';
import { toast } from 'react-hot-toast';
import KYCButton from './KYCButton';
import { ONBOARDING_EVENTS, ONBOARDING_PAGE_TYPE } from './constants';
import { useAnalytics } from '../../hooks/useAnalytics';
import { useSearchParams } from 'react-router-dom';
import { Mail } from '@mui/icons-material';

interface PhoneAuthProps {
  isSignupFlow: boolean;
  setIsSignupFlow: (isSignupFlow: boolean) => void;
}

const PhoneAuth: React.FC<PhoneAuthProps> = ({ isSignupFlow, setIsSignupFlow }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [showOtpField, setShowOtpField] = useState(false);
  const { handleNextPage } = useOnboardingNavigate();
  const [isOtpResend, setIsOtpResend] = useState(false);
  const [timeForOtpResend, setTimeForOtpResend] = useState(30);
  const [searchParams] = useSearchParams();
  const { track } = useAnalytics();
  const [isInputDisabled, setIsInputDisabled] = useState(false);

  const { setNextPage } = useOnboardingStore(({ setNextPage }) => ({
    setNextPage,
  }));

  const schema = yup.object().shape({
    phone: yup
      .string()
      .phone('IN', 'Please enter a valid phone number')
      .required('Phone Number is required')
      .nullable(),
    otp: yup.string().when(['showOtp'], {
      is: (showOtp: boolean) => showOtp === true,
      then: () =>
        yup
          .string()
          .required('OTP is required')
          .matches(/^\d{4}$/, 'Must be a 4-digit OTP'),
      otherwise: () => yup.string().nullable(),
    }),
    showOtp: yup.boolean().default(false),
  });

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: { phone: '', otp: '', showOtp: false },
  });

  const phone = watch('phone');
  const otp = watch('otp');

  const submitPhone = (phone: string) => {
    return NetworkManager.Call({
      method: 'post',
      path: NetworkManager.PATH.phoneOTP,
      data: { phone },
    });
  };

  const submitOTP = (phone: string, otp: string) => {
    const referralCodeValue = searchParams.get('refCode');
    const rmMappingCodeValue = searchParams.get('rmMappingCode');
    const data = {
      phone,
      otp,
      ...(referralCodeValue &&
        referralCodeValue !== '' && {
          referralCode: referralCodeValue,
        }),
      ...(rmMappingCodeValue && {
        rmMappingCode: rmMappingCodeValue,
      }),
    };

    return NetworkManager.Call({
      method: 'post',
      path: NetworkManager.PATH.verifyPhoneOTP,
      data,
    });
  };

  const onSubmit = async (data: Record<string, any>) => {
    const { phone, otp } = data;
    setIsLoading(true);

    try {
      if (!showOtpField) {
        // Send OTP
        const phoneRes = await submitPhone(phone);
        if (!phoneRes?.error) {
          handleEntryEvent(ONBOARDING_EVENTS.Phone_Submit, { phone: phone, status: 'success' });
          toast.success('OTP sent successfully');
          setShowOtpField(true);
          setValue('showOtp', true);
          setTimeForOtpResend(30);
          setIsOtpResend(false);
          setIsInputDisabled(true);
        } else {
          handleEntryEvent(ONBOARDING_EVENTS.Phone_Submit, {
            phone: phone,
            status: 'failure',
            error: phoneRes.error,
          });
          throw new Error(phoneRes.error);
        }
      } else {
        // Verify OTP
        const otpRes = await submitOTP(phone, otp);
        if (otpRes?.code?.toString().startsWith('2')) {
          handleEntryEvent(ONBOARDING_EVENTS.Phone_OTP_Submit, { phone: phone, status: 'success' });
          if (otpRes.nextPage === ONBOARDING_PAGE_TYPE.EMAIL_VERIFICATION) {
            setIsSignupFlow(true);
          }
          handleNextPage(otpRes.nextPage, otpRes.data);
        } else {
          handleEntryEvent(ONBOARDING_EVENTS.Phone_OTP_Submit, {
            phone: phone,
            status: 'failure',
            error: otpRes.error,
          });
          toast.error('Invalid OTP');
        }
      }
    } catch (error: any) {
      if (error?.response?.status?.toString().startsWith('5')) {
        toast.error('Something went wrong! Please try again');
      } else if (error?.response?.status?.toString().startsWith('4')) {
        toast.error(error?.response?.data?.message);
      } else {
        toast.error('Something went wrong! Please try again');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleEntryEvent = (event: string, obj: object) => {
    track({ eventName: event, eventProperties: obj });
  };

  React.useEffect(() => {
    if (timeForOtpResend > 0) {
      const timerId = setTimeout(() => {
        setTimeForOtpResend(timeForOtpResend - 1);
      }, 1000);
      return () => clearTimeout(timerId);
    } else {
      setIsOtpResend(true);
    }
  }, [timeForOtpResend]);

  const handleResend = async () => {
    if (!phone) return;

    setIsLoading(true);
    setIsOtpResend(false);
    setTimeForOtpResend(30);

    handleEntryEvent(ONBOARDING_EVENTS.Phone_OTP_Resend, { phone });

    try {
      const otpRes = await submitPhone(phone);
      if (otpRes?.code?.toString().startsWith('2')) {
        toast.success('OTP sent successfully');
      } else {
        throw new Error(otpRes.error || 'Failed to send OTP');
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to send OTP');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangeNumber = () => {
    setShowOtpField(false);
    setValue('otp', '');
    setValue('showOtp', false);
    setIsInputDisabled(false);
  };

  return (
    <div className="flex flex-col gap-8">
      <div>
        <h2 className="text-2xl font-semibold mb-2">Get started</h2>
        <p className="text-gray-600">Welcome to Tap Partner!</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
        <Field label="Phone Number" error={errors.phone?.message}>
          <Input
            type="text"
            numericOnly={true}
            placeholder="9501xxxxxx"
            maxLength={10}
            // readOnly instead of disabled: react-hook-form clears the value of a disabled input on blur
            readOnly={isInputDisabled}
            className={isInputDisabled ? 'text-gray-500 cursor-not-allowed' : undefined}
            {...register('phone')}
            prefixNode={<span className="text-slate-600 font-semibold text-sm">+91</span>}
            onFocus={() => {
              if (!isInputDisabled) {
                handleEntryEvent(ONBOARDING_EVENTS.Partner_Phone_Number_Enter, {});
              }
            }}
          />
        </Field>

        {showOtpField && (
          <div className="flex flex-col gap-1">
            <div className="flex justify-between items-center">
              <label className="text-sm text-gray-600">OTP</label>
              {timeForOtpResend > 0 ? (
                <span className="text-sm text-gray-500">Resend OTP in {timeForOtpResend}s</span>
              ) : (
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={isLoading || !isOtpResend}
                  className="text-sm text-green-800 hover:underline"
                >
                  Resend OTP
                </button>
              )}
            </div>
            <Input
              type="text"
              numericOnly={true}
              placeholder="Enter 4-digit OTP"
              maxLength={4}
              {...register('otp')}
            />
            {errors.otp?.message && (
              <span className="text-red-500 text-sm">{errors.otp.message}</span>
            )}
          </div>
        )}

        <KYCButton
          isLoading={isLoading}
          text={!showOtpField ? 'Get OTP' : 'Verify & Continue'}
          disabled={!phone || phone.length !== 10 || (showOtpField && (!otp || otp.length !== 4))}
          className="!mt-4"
        />
      </form>

      {!isSignupFlow && (
        <>
          <div className="flex items-center gap-4">
            <div className="h-[1px] flex-1 bg-gray-200" />
            <span className="text-gray-400">or</span>
            <div className="h-[1px] flex-1 bg-gray-200" />
          </div>

          <button
            type="button"
            onClick={() => {
              setNextPage(ONBOARDING_PAGE_TYPE.EMAIL_VERIFICATION);
            }}
            className="flex items-center justify-center gap-2 w-full py-3 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50"
          >
            <Mail className="w-4 h-4" />
            Login via Email
          </button>
        </>
      )}
      {showOtpField && (
        <div className="flex flex-col items-center gap-2">
          <button
            type="button"
            onClick={handleChangeNumber}
            className="text-sm text-green-800 hover:underline"
          >
            Change Phone Number
          </button>
        </div>
      )}
    </div>
  );
};

export default PhoneAuth;
